/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useStore } from '../../../../../store';
import { useMapContext } from '../../context/map-context';

interface ICenterPoints {
  perpendicularPointTLandTR: [number, number];
  perpendicularPointTLandBL: [number, number];
  perpendicularPointBLandBR: [number, number];
  perpendicularPointBRandTR: [number, number];
}

interface IPixelsPerLengthRange {
  onePixelInRangeLat: number;
  onePixelInRangeLon: number;
}

export const XYAxes = ():JSX.Element | null => {
  const {
    configurationValues, dimensions, imageAngle, rangePerDegree,
  } = useMapContext();
  const map = useMap();
  const { mapOrigin, mapImage } = useStore();
  const { xofffset, yoffset, pixelsPerLength } = mapImage.data || {};
  const [lines, setLines] = useState(null);
  const [zeroPoint, setZeroPoint] = useState<[number, number]>([
    Number(mapOrigin.data.latitude),
    Number(mapOrigin.data.longitude),
  ]);

  function rotatePoint(
    point: [number, number],
    center: [number, number],
    angle: number,
  ): [number, number] | null {
    const angleRad = (angle * Math.PI) / 180;
    const originLat = Number(point[0]);
    const originLon = Number(point[1]);

    if (!Number.isFinite(originLat) || !Number.isFinite(originLon)) {
      return null;
    }

    // Project lat/lng to pixel coordinates
    const pointProjected = map.project(L.latLng(point));
    const centerProjected = map.project(L.latLng(center));

    const deltaX = pointProjected.x - centerProjected.x;
    const deltaY = pointProjected.y - centerProjected.y;

    // Rotate using trigonometric formulas
    const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
    const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

    // Translate back to pixel coordinates and then to lat/lng
    const rotatedPointProjected = L.point(
      centerProjected.x + rotatedX,
      centerProjected.y + rotatedY,
    );

    return map.unproject(rotatedPointProjected);
  }

  useEffect(() => {
    setZeroPoint([Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)]);
  }, [mapOrigin.data]);
  const [centerPoints, setCenterPoints] = useState<ICenterPoints| null>(null);
  const [pixelsPerLengthRange, setPixelsPerLengthRange] = useState<IPixelsPerLengthRange
  | null>(null);

  useEffect(() => {
    if (mapImage.data.xofffset === undefined || !dimensions?.height) return;

    const x = Number(dimensions?.width) - Number(xofffset);
    const y = Number(dimensions?.height) - Number(yoffset);

    const xLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(xofffset); // x1
    const yLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(yoffset); // y1

    const xLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(x); // x2
    const yLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(y); // y2

    const rangePerDegreeLat = rangePerDegree.lon;
    const rangePerDegreeLon = rangePerDegree.lat;

    const latOffset = xLengthInFt1 / rangePerDegreeLat;
    const lonOffset = yLengthInFt1 / rangePerDegreeLon;

    const latOffset2 = xLengthInFt2 / rangePerDegreeLat;
    const lonOffset2 = yLengthInFt2 / rangePerDegreeLon;

    const onePixelInRangeLat = 1 / Number(pixelsPerLength) / rangePerDegreeLat;
    const onePixelInRangeLon = 1 / Number(pixelsPerLength) / rangePerDegreeLon;
    setPixelsPerLengthRange({ onePixelInRangeLat, onePixelInRangeLon });

    const topLeft: [number, number] = [zeroPoint[0] + lonOffset2, zeroPoint[1] - latOffset];
    const topRight: [number, number] = [zeroPoint[0] + lonOffset2, zeroPoint[1] + latOffset2];
    const bottomLeft: [number, number] = [zeroPoint[0] - lonOffset, zeroPoint[1] - latOffset];
    const bottomRight: [number, number] = [zeroPoint[0] - lonOffset, zeroPoint[1] + latOffset2];

    const rotatedTopLeft = rotatePoint(topLeft, zeroPoint, imageAngle);
    const rotatedTopRight = rotatePoint(topRight, zeroPoint, imageAngle);
    const rotatedBottomLeft = rotatePoint(bottomLeft, zeroPoint, imageAngle);
    const rotatedBottomRight = rotatePoint(bottomRight, zeroPoint, imageAngle);

    const findPerpendicularPoint = (
      zero: [number, number],
      pointA: [number, number],
      pointB: [number, number],
    ): [number, number] => {
      const zeroProjected = map.project(L.latLng(zero));
      const pointAProjected = map.project(L.latLng(pointA));
      const pointBProjected = map.project(L.latLng(pointB));

      // Extract x and y values
      const { x: x0, y: y0 } = zeroProjected;
      const { x: x1, y: y1 } = pointAProjected;
      const { x: x2, y: y2 } = pointBProjected;

      // Line vector
      const lineVector = [x2 - x1, y2 - y1];

      // Point vector
      const pointVector = [x0 - x1, y0 - y1];

      // Dot product
      const dotProduct = pointVector[0] * lineVector[0] + pointVector[1] * lineVector[1];

      // Magnitude squared of line vector
      const lineVectorMagnitudeSquared = lineVector[0]
      * lineVector[0] + lineVector[1] * lineVector[1];

      // Projection factor
      const projectionFactor = dotProduct / lineVectorMagnitudeSquared;

      // Projection point in projected coordinates
      const projectionPoint = {
        x: x1 + projectionFactor * lineVector[0],
        y: y1 + projectionFactor * lineVector[1],
      };

      // Convert back to lat/lng
      const projectedPointLatLng = map.unproject(L.point(projectionPoint.x, projectionPoint.y));
      return [projectedPointLatLng.lat, projectedPointLatLng.lng];
    };

    if (!rotatedTopLeft || !rotatedBottomLeft || !rotatedTopRight || !rotatedBottomRight) {
      return;
    }

    const perpendicularPointTLandTR = findPerpendicularPoint(
      zeroPoint,
      rotatedTopLeft,
      rotatedTopRight,
    );
    const perpendicularPointTLandBL = findPerpendicularPoint(
      zeroPoint,
      rotatedTopLeft,
      rotatedBottomLeft,
    );
    const perpendicularPointBLandBR = findPerpendicularPoint(
      zeroPoint,
      rotatedBottomLeft,
      rotatedBottomRight,
    );
    const perpendicularPointBRandTR = findPerpendicularPoint(
      zeroPoint,
      rotatedBottomRight,
      rotatedTopRight,
    );

    setCenterPoints({
      perpendicularPointTLandTR,
      perpendicularPointTLandBL,
      perpendicularPointBLandBR,
      perpendicularPointBRandTR,
    });
  }, [mapImage.data, dimensions, imageAngle]);

  const createCustomIcon = (text:string, style: string) => L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="border-radius: 4px; color: #3800EE; font-size: 12px; font-weight:500; ${style}">${text}</div>`,
  });

  useEffect(() => {
    const updateLines = () => {
      // main lines
      const latLine = [
        [centerPoints?.perpendicularPointTLandBL[1], centerPoints?.perpendicularPointTLandBL[0]],
        [centerPoints?.perpendicularPointBRandTR[1], centerPoints?.perpendicularPointBRandTR[0]],
      ];

      const lngLine = [
        [centerPoints?.perpendicularPointBRandTR[1], centerPoints?.perpendicularPointBRandTR[0]],
        [centerPoints?.perpendicularPointTLandBL[1], centerPoints?.perpendicularPointTLandBL[0]],
      ];

      setLines({
        latLine,
        lngLine,
      });
    };

    map.on('move', updateLines);
    updateLines();

    return () => {
      map.off('move', updateLines);
    };
  }, [map, mapOrigin, configurationValues?.visibility?.llGrid, centerPoints]);

  const XYLabelOffset = (centerPoints?.perpendicularPointBRandTR?.[0] || 0)
    < (centerPoints?.perpendicularPointTLandTR?.[0] || 0) ? 20 : -20;

  return (
    (configurationValues?.visibility?.xyAxes && centerPoints) ? (
      <>
        <Polyline
          positions={[centerPoints?.perpendicularPointTLandTR,
            Number(yoffset) > 0 ? centerPoints.perpendicularPointBLandBR : zeroPoint]}
          pathOptions={{ color: '#3800EE', weight: 2 }}
        />
        <Polyline
          positions={[centerPoints.perpendicularPointBRandTR,
            Number(xofffset) > 0 ? centerPoints.perpendicularPointTLandBL : zeroPoint]}
          pathOptions={{ color: '#3800EE', weight: 2 }}
        />

        { configurationValues?.visibility?.xyAxesLabels
        && centerPoints?.perpendicularPointBRandTR && (
          <Marker
            position={[centerPoints?.perpendicularPointBRandTR[0],
              centerPoints?.perpendicularPointBRandTR[1] + 0]}
            icon={createCustomIcon('X', `margin-left: ${XYLabelOffset}px`)}
          />
        )}
        {configurationValues?.visibility?.xyAxesLabels
        && centerPoints?.perpendicularPointTLandTR && (
          <Marker
            position={[centerPoints?.perpendicularPointTLandTR[0] + 0,
              centerPoints?.perpendicularPointTLandTR[1]]}
            icon={createCustomIcon('Y', `margin-top: ${-XYLabelOffset}px`)}
          />
        )}

      </>
    ) : null
  );
};
